/**
 * **Tiers by size** — optional, and hidden until asked for
 * (`builder-engine-and-consistency.md` §6.8 item 3, board cb-19 cells 2–3).
 *
 * A line with no tiers shows one quiet dashed row. A line with tiers shows the
 * grid: *From* is never typed (the row above's *Up to* plus one), the first
 * tier's rate is the line's own, and the last tier runs on for ever. The check
 * strip underneath is the quantity at four sizes the editor chooses
 * (`tierCheckSizes`), worked out as somebody types. The tiers are stored as
 * bands by `tiersToBands`, so a bigger job can never come to less.
 *
 * On a phone (cb-22 cell 4) the grid becomes **one card per tier** — `Tier 2`,
 * `from 3,001 sq ft` and Remove over a two-up Up to / Per at 46px — and the
 * check strip is two by two. Same tiers, same callbacks: only the drawing
 * changes, because four columns of 34px boxes do not fit 358px.
 */
import { formatNumber } from '../../lib/format.js'
import { cn } from '../../lib/cn.js'
import { usePhone } from '../../lib/use-narrow.js'
import { FieldShell } from '../../components/fields.js'
import { IconChevronDown, IconChevronRight, IconClose } from '../../frame/icons.js'
import {
  ADD_TIER, AND_UP, REMOVE, TIER_CARD, TIER_CARD_FROM, TIER_FROM, TIER_PER, TIER_UP_TO, TIERS_ADD,
  TIERS_EXPLAIN, TIERS_REMOVE, TIERS_SUB, TIERS_TITLE,
} from '../copy.js'
import { UnitInput } from './unit-input.js'
import { formatTierNumber, readNumber, tierCheckSizes, toTiers, type DraftTier } from './tiers.js'

export interface TiersEditorProps {
  tiers: DraftTier[] | null
  onChange: (tiers: DraftTier[] | null) => void
  /** The line's own rate, which the first tier starts from. */
  rate: string
  qtyUnit: string
  measureUnit: string
  sample: number
  /** What the rule as typed makes at a measure — `quantityFor`, never re-derived here. */
  quantityAt: (measure: number) => number
}

const GRID = 'grid grid-cols-[76px_minmax(0,1fr)_minmax(0,1fr)_22px] items-center gap-2.5'

export function TiersEditor({
  tiers, onChange, rate, qtyUnit, measureUnit, sample, quantityAt,
}: TiersEditorProps) {
  const phone = usePhone()
  if (tiers === null || tiers.length < 2) {
    return (
      <button
        type="button"
        onClick={() => onChange([
          // A first ceiling three times the sample: somewhere to start, typed over at once.
          { upTo: formatTierNumber(Math.max(1, Math.round(sample * 3))), qtyPerUnit: rate },
          { upTo: null, qtyPerUnit: rate },
        ])}
        className="flex items-center gap-2.5 rounded-[10px] border border-dashed border-line-strong bg-surface px-3 py-[9px] text-left"
      >
        <span className="text-text-faint"><IconChevronRight size={14} /></span>
        <span className="flex flex-col">
          <b className="text-[12.5px] font-semibold text-text-muted">{TIERS_TITLE}</b>
          <span className="text-[11.5px] text-text-faint">{TIERS_SUB}</span>
        </span>
        <span className="ml-auto whitespace-nowrap text-[12.5px] font-semibold text-accent">{TIERS_ADD}</span>
      </button>
    )
  }

  const set = (index: number, patch: Partial<DraftTier>) =>
    onChange(tiers.map((tier, at) => (at === index ? { ...tier, ...patch } : tier)))

  const remove = (index: number) => {
    const next = tiers.filter((_, at) => at !== index)
    // The tier that is now last runs on for ever.
    next[next.length - 1] = { ...next[next.length - 1]!, upTo: null }
    onChange(next.length < 2 ? null : next)
  }

  const add = () => {
    const last = tiers[tiers.length - 1]!
    const before = tiers[tiers.length - 2]
    const floor = before?.upTo ? readNumber(before.upTo) : 0
    const ceiling = floor + Math.max(1, Math.round(sample * 2))
    onChange([
      ...tiers.slice(0, -1),
      { ...last, upTo: formatTierNumber(ceiling) },
      { upTo: null, qtyPerUnit: last.qtyPerUnit },
    ])
  }

  const sizes = tierCheckSizes(sample, toTiers(tiers))
  const fromOf = (index: number) => (index === 0 ? 0 : readNumber(tiers[index - 1]!.upTo ?? '0') + 1)

  if (phone) {
    return (
      <section className="flex flex-col gap-3" aria-label={TIERS_TITLE}>
        <div className="flex items-center gap-2">
          <span className="text-text"><IconChevronDown size={14} /></span>
          <b className="text-[15px] font-bold text-text">{TIERS_TITLE}</b>
          <button type="button" onClick={() => onChange(null)}
            className="ml-auto text-[13px] font-semibold text-danger">
            {TIERS_REMOVE}
          </button>
        </div>
        <p className="m-0 -mt-1 text-[12.5px] leading-[1.45] text-text-muted">{TIERS_EXPLAIN(measureUnit)}</p>

        {tiers.map((tier, index) => {
          const last = tier.upTo === null
          return (
            <div key={index} data-tier-card=""
              className="flex flex-col gap-[9px] rounded-[12px] border border-line bg-surface px-3 pb-3 pt-[11px]">
              <div className="flex items-center gap-2">
                <b className="text-[13.5px] font-bold text-text">{TIER_CARD(index + 1)}</b>
                <span className="tnum text-[12.5px] text-text-muted">
                  {TIER_CARD_FROM(formatNumber(fromOf(index)), measureUnit)}
                </span>
                {index > 0 && (
                  <button type="button" aria-label={`Remove tier ${index + 1}`} onClick={() => remove(index)}
                    className="ml-auto text-[12.5px] font-semibold text-danger">
                    {REMOVE}
                  </button>
                )}
              </div>
              <div className="grid grid-cols-2 gap-2.5">
                <FieldShell label={TIER_UP_TO}>
                  {last
                    ? <UnitInput label={`Tier ${index + 1} up to`} value="" placeholder={AND_UP} readOnly size="phone" />
                    : (
                      <UnitInput label={`Tier ${index + 1} up to`} value={tier.upTo ?? ''} size="phone"
                        unit={measureUnit} onChange={(value) => set(index, { upTo: value })} />
                    )}
                </FieldShell>
                <FieldShell label={TIER_PER(measureUnit)}>
                  <UnitInput label={`Tier ${index + 1} ${TIER_PER(measureUnit)}`} value={tier.qtyPerUnit}
                    size="phone" unit={qtyUnit} onChange={(value) => set(index, { qtyPerUnit: value })} />
                </FieldShell>
              </div>
            </div>
          )
        })}
        <button type="button" onClick={add} className="self-start text-[13.5px] font-semibold text-accent">
          {ADD_TIER}
        </button>

        <div data-tier-check=""
          className="grid grid-cols-2 gap-x-3.5 gap-y-2.5 rounded-[12px] border border-line-soft bg-surface-alt px-3.5 py-[11px]">
          {sizes.map((size) => (
            <span key={size} className="flex flex-col gap-0.5 text-[12px] text-text-muted">
              {formatNumber(size)} {measureUnit}
              <b className="tnum text-[16px] text-text">{formatNumber(quantityAt(size))} {qtyUnit}</b>
            </span>
          ))}
        </div>
      </section>
    )
  }

  return (
    <section className="flex flex-col gap-2.5" aria-label={TIERS_TITLE}>
      <div className="flex items-center gap-2">
        <span className="text-text"><IconChevronDown size={14} /></span>
        <b className="text-[14px] font-bold text-text">{TIERS_TITLE}</b>
        <button type="button" onClick={() => onChange(null)}
          className="ml-auto text-[12.5px] font-semibold text-danger">
          {TIERS_REMOVE}
        </button>
      </div>
      <p className="m-0 -mt-1 text-[12px] leading-[1.45] text-text-muted">{TIERS_EXPLAIN(measureUnit)}</p>

      <div>
        <div className={cn(GRID, 'pb-0.5 text-label uppercase text-text-faint')}>
          <span>{TIER_FROM}</span><span>{TIER_UP_TO}</span><span>{TIER_PER(measureUnit)}</span><span />
        </div>
        {tiers.map((tier, index) => {
          const from = fromOf(index)
          const last = tier.upTo === null
          return (
            <div key={index} className={cn(GRID, 'py-[5px]')}>
              <span className="tnum text-[13px] text-text-faint">{formatNumber(from)}</span>
              {last
                ? <UnitInput label={`Tier ${index + 1} up to`} value={AND_UP} readOnly size="row" />
                : (
                  <UnitInput label={`Tier ${index + 1} up to`} value={tier.upTo ?? ''} size="row"
                    unit={measureUnit} onChange={(value) => set(index, { upTo: value })} />
                )}
              <UnitInput label={`Tier ${index + 1} ${TIER_PER(measureUnit)}`} value={tier.qtyPerUnit}
                size="row" unit={qtyUnit} onChange={(value) => set(index, { qtyPerUnit: value })} />
              {index === 0
                ? <span className="w-[18px]" />
                : (
                  <button type="button" aria-label={`Remove tier ${index + 1}`} onClick={() => remove(index)}
                    className="flex w-[18px] items-center text-text-faint hover:text-text">
                    <IconClose size={14} />
                  </button>
                )}
            </div>
          )
        })}
        <button type="button" onClick={add}
          className="pl-[86px] pt-1 text-[12.5px] font-semibold text-accent">
          {ADD_TIER}
        </button>
      </div>

      <div data-tier-check="" className="grid grid-cols-4 gap-2 rounded-[10px] border border-line-faint bg-surface-alt px-3 py-[9px]">
        {sizes.map((size) => (
          <span key={size} className="flex flex-col gap-0.5 text-[11px] text-text-muted">
            {formatNumber(size)} {measureUnit}
            <b className="tnum text-[14px] text-text">{formatNumber(quantityAt(size))} {qtyUnit}</b>
          </span>
        ))}
      </div>
    </section>
  )
}
